/**
 * Project Creation Wizard
 * 
 * Phase D2: Create Projects via fixed 3-step wizard.
 * Steps: Metadata → Select Library documents → Confirm
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFirestore } from 'firebase/firestore';
import { 
  listDocuments, 
  createProject, 
  createJob,
  type RbDocument,
  type ProjectIntent 
} from '@threadlock/data';
import { ROUTES } from '@threadlock/routing';
import { useWorkspaceState } from '../../config/workspace';
import { useAuthState } from '../../config/auth';

type WizardStep = 'metadata' | 'documents' | 'confirm';

export default function ProjectNewPage() {
  const navigate = useNavigate();
  const { activeWorkspaceId } = useWorkspaceState();
  const { user } = useAuthState();
  const workspaceId = activeWorkspaceId;
  const userId = user?.uid;
  const [currentStep, setCurrentStep] = useState<WizardStep>('metadata');
  
  // Form state
  const [title, setTitle] = useState('');
  const [jurisdiction, setJurisdiction] = useState('');
  const [intent, setIntent] = useState<ProjectIntent>('RFP');
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([]);
  
  // Documents list
  const [documents, setDocuments] = useState<RbDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  
  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load documents when moving to documents step
  useEffect(() => {
    if (currentStep === 'documents' && workspaceId) {
      setDocumentsLoading(true);
      const db = getFirestore();
      
      listDocuments(db, workspaceId)
        .then((docs) => {
          // Filter to only show ready documents
          const readyDocs = docs.filter(d => d.status === 'ready');
          setDocuments(readyDocs);
          setDocumentsLoading(false);
        })
        .catch((err) => {
          console.error('[ProjectNewPage] Error loading documents:', err);
          setError('Failed to load documents');
          setDocumentsLoading(false);
        });
    }
  }, [currentStep, workspaceId]);

  const handleMetadataNext = () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!jurisdiction.trim()) {
      setError('Jurisdiction is required');
      return;
    }
    setError(null);
    setCurrentStep('documents');
  };

  const handleDocumentsNext = () => {
    if (selectedDocIds.length === 0) {
      setError('At least one document must be selected');
      return;
    }
    setError(null);
    setCurrentStep('confirm');
  };

  const handleSubmit = async () => {
    if (!workspaceId || !userId) {
      setError('Workspace or user not available');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const db = getFirestore();
      const projectId = `proj_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Create project
      await createProject(db, projectId, {
        workspaceId,
        title: title.trim(),
        jurisdiction: jurisdiction.trim(),
        intent,
        sourceDocumentIds: selectedDocIds,
        createdByUserId: userId,
      });

      // Create audit job for project_created
      const projectCreatedJobId = `job_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      await createJob(db, projectCreatedJobId, {
        workspaceId,
        type: 'project',
        action: 'project_created',
        status: 'ok',
        message: `Project "${title}" created`,
        meta: {
          projectId,
        },
        createdByUserId: userId,
      });

      // Create audit job for documents_associated
      const docsAssociatedJobId = `job_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      await createJob(db, docsAssociatedJobId, {
        workspaceId,
        type: 'project',
        action: 'documents_associated',
        status: 'ok',
        message: `${selectedDocIds.length} document(s) associated with project`,
        meta: {
          projectId,
          documentIds: selectedDocIds,
        },
        createdByUserId: userId,
      });

      // Navigate to project detail
      navigate(ROUTES.PROJECT_DETAIL.replace(':projectId', projectId));
    } catch (err) {
      console.error('[ProjectNewPage] Error creating project:', err);
      setError(err instanceof Error ? err.message : 'Failed to create project');
      setSubmitting(false);
    }
  };

  const selectedDocuments = documents.filter(d => selectedDocIds.includes(d.id));

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-neutral-900">New Project</h1>
        <p className="mt-2 text-sm text-neutral-600">
          Create a new response project
        </p>
      </div>

      {/* Step indicator */}
      <div className="mb-8">
        <nav aria-label="Progress">
          <ol className="flex items-center">
            <li className="relative pr-8 flex items-center">
              <div
                className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                  currentStep === 'metadata' ? 'border-blue-600 bg-blue-600 text-white' : 'border-green-600 bg-green-600 text-white'
                }`}
              >
                {currentStep === 'metadata' ? '1' : '✓'}
              </div>
              <span className="ml-2 text-sm font-medium text-neutral-900">Metadata</span>
            </li>
            <li className="relative pr-8 flex items-center">
              <div
                className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                  currentStep === 'documents' ? 'border-blue-600 bg-blue-600 text-white' :
                  currentStep === 'confirm' ? 'border-green-600 bg-green-600 text-white' :
                  'border-neutral-300 bg-white text-neutral-500'
                }`}
              >
                {currentStep === 'confirm' ? '✓' : '2'}
              </div>
              <span className={`ml-2 text-sm font-medium ${currentStep !== 'metadata' ? 'text-neutral-900' : 'text-neutral-500'}`}>
                Documents
              </span>
            </li>
            <li className="relative flex items-center">
              <div
                className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                  currentStep === 'confirm' ? 'border-blue-600 bg-blue-600 text-white' : 'border-neutral-300 bg-white text-neutral-500'
                }`}
              >
                3
              </div>
              <span className={`ml-2 text-sm font-medium ${currentStep === 'confirm' ? 'text-neutral-900' : 'text-neutral-500'}`}>
                Confirm
              </span>
            </li>
          </ol>
        </nav>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Step 1: Metadata */}
      {currentStep === 'metadata' && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-neutral-900 mb-4">Project Information</h2>
          
          <div className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-neutral-700 mb-1">
                Project Title *
              </label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Healthcare IT System RFP Response"
              />
            </div>

            <div>
              <label htmlFor="jurisdiction" className="block text-sm font-medium text-neutral-700 mb-1">
                Jurisdiction *
              </label>
              <input
                type="text"
                id="jurisdiction"
                value={jurisdiction}
                onChange={(e) => setJurisdiction(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., California, Federal, New York"
              />
            </div>

            <div>
              <label htmlFor="intent" className="block text-sm font-medium text-neutral-700 mb-1">
                Intent *
              </label>
              <select
                id="intent"
                value={intent}
                onChange={(e) => setIntent(e.target.value as ProjectIntent)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="RFP">RFP (Request for Proposal)</option>
                <option value="RFI">RFI (Request for Information)</option>
                <option value="GRANT">Grant Application</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={handleMetadataNext}
              className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Select Documents */}
      {currentStep === 'documents' && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-neutral-900 mb-4">Select Library Documents</h2>
          <p className="text-sm text-neutral-600 mb-4">
            Choose existing documents from your library to associate with this project.
          </p>

          {documentsLoading ? (
            <div className="py-8 text-center text-neutral-600">Loading documents...</div>
          ) : documents.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-neutral-600 mb-2">No documents available in your library.</p>
              <p className="text-sm text-neutral-500">Upload documents to the Library first.</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {documents.map((doc) => (
                <label
                  key={doc.id}
                  className="flex items-center p-3 border border-neutral-200 rounded-lg hover:bg-neutral-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedDocIds.includes(doc.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedDocIds([...selectedDocIds, doc.id]);
                      } else {
                        setSelectedDocIds(selectedDocIds.filter(id => id !== doc.id));
                      }
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-neutral-300 rounded"
                  />
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-medium text-neutral-900">{doc.filename}</p>
                    <p className="text-xs text-neutral-500">
                      {doc.documentType} • {doc.jurisdiction}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          )}

          <div className="mt-6 flex justify-between">
            <button
              type="button"
              onClick={() => setCurrentStep('metadata')}
              className="px-4 py-2 border border-neutral-300 text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Back
            </button>
            <button
              type="button"
              onClick={handleDocumentsNext}
              disabled={selectedDocIds.length === 0}
              className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-neutral-300 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Confirm */}
      {currentStep === 'confirm' && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-neutral-900 mb-4">Confirm Project Creation</h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-neutral-700">Title</h3>
              <p className="text-sm text-neutral-900">{title}</p>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-neutral-700">Jurisdiction</h3>
              <p className="text-sm text-neutral-900">{jurisdiction}</p>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-neutral-700">Intent</h3>
              <p className="text-sm text-neutral-900">{intent}</p>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-neutral-700 mb-2">Associated Documents ({selectedDocuments.length})</h3>
              <ul className="list-disc list-inside space-y-1">
                {selectedDocuments.map((doc) => (
                  <li key={doc.id} className="text-sm text-neutral-900">{doc.filename}</li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-6 flex justify-between">
            <button
              type="button"
              onClick={() => setCurrentStep('documents')}
              disabled={submitting}
              className="px-4 py-2 border border-neutral-300 text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Back
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-neutral-300 disabled:cursor-not-allowed"
            >
              {submitting ? 'Creating...' : 'Create Project'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}